import { useState, useEffect, useRef, useCallback } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { breedService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import type { Breed } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";
import {
  standardCurveService,
  validateCurvePoints,
  curveMetricLabel,
  CURVE_METRICS,
  type CurveMetric,
  type CurvePoint,
  type CurveSource,
  type StandardCurve,
} from "@/services/standardCurveService";

const SOURCE_LABEL: Record<CurveSource, string> = {
  breed: "This breed's own curve",
  farm: "Farm-wide curve",
  default: "Generic default shipped with the app",
  // Not "default": there is nothing to fall back to for the gram metrics, and
  // saying "default" here would tell the farm it is being measured against
  // something. Without this key the badge rendered the literal text
  // "In force: undefined" and appended "undefined" to a className.
  none: "No standard set",
};

// Looked up through a function, not indexed directly. `source` is a raw string
// off the API, and a Record only protects it while the client union matches the
// server's list — which is the assumption that failed when the server gained
// three metrics and this file had two. Indexing an absent key is what rendered
// the literal text "In force: undefined" and appended "undefined" to a
// className.
const sourceLabel = (s: string): string =>
  SOURCE_LABEL[s as CurveSource] ?? "Unrecognised source";
const sourceTone = (s: string): string =>
  SOURCE_TONE[s as CurveSource] ?? SOURCE_TONE.none;

const SOURCE_TONE: Record<CurveSource, string> = {
  breed: "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400",
  farm: "bg-blue-light-50 text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-400",
  default: "bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400",
  none: "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400",
};

export default function StandardCurvesPage() {
  const isOwner = useIsOwner();
  const fileRef = useRef<HTMLInputElement>(null);
  // Monotonic id for in-flight loads. Switching measure or breed fires a new GET
  // while the old one may still be running, and responses arrive in whatever
  // order the network gives them — without this the slower one wins and the
  // table shows a curve the selectors no longer name.
  const loadSeq = useRef(0);

  const [breeds, setBreeds] = useState<Breed[]>([]);
  // Everything the farm has actually stored, so the owner can see at a glance
  // which breeds are set up rather than probing the dropdowns one by one.
  const [savedCurves, setSavedCurves] = useState<StandardCurve[]>([]);
  const [metric, setMetric] = useState<CurveMetric>("hdp");
  // null = the farm-wide curve, which every breed without its own falls back to.
  const [breedId, setBreedId] = useState<number | null>(null);

  const [points, setPoints] = useState<CurvePoint[]>([]);
  const [source, setSource] = useState<CurveSource>("default");
  // Whether rows exist at the level currently selected, as opposed to inherited
  // from further down the chain. Only a stored curve can be removed.
  const [stored, setStored] = useState(false);
  const [loading, setLoading] = useState(true);
  // Set whenever the table differs from what was loaded or last saved, so
  // switching measure/breed can warn before throwing away an imported guide.
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // No `!`. The non-null assertion crashed the page: saving a curve for a
  // metric the client did not know about (which was possible, because
  // egg_weight values happen to be under the old flat bound) then clicking its
  // chip set `metric` to a string absent from CURVE_METRICS, and every
  // `metricInfo.help` / `.unit` below threw. Falling back keeps the page
  // standing on a metric added server-side before the client catches up.
  const metricInfo =
    CURVE_METRICS.find((m) => m.value === metric) ?? {
      value: metric,
      label: metric,
      unit: "",
      max: 100,
      help: "",
    };

  // Only hdp and mortality ship a generic curve. The three gram metrics
  // deliberately do not — see CurveSourceNone in standardCurveService.
  const hasShippedDefault = metric === "hdp" || metric === "mortality";

  useEffect(() => {
    breedService
      .list()
      .then(setBreeds)
      .catch(() => setBreeds([]));
  }, []);

  const loadSaved = useCallback(() => {
    standardCurveService
      .list()
      .then((all) => setSavedCurves(all.filter((c) => c.stored)))
      .catch(() => setSavedCurves([]));
  }, []);

  useEffect(() => {
    loadSaved();
  }, [loadSaved]);

  const loadCurve = useCallback(
    async (keepNotice = false) => {
      const seq = ++loadSeq.current;
      setLoading(true);
      setError("");
      if (!keepNotice) setNotice("");
      try {
        const curve = await standardCurveService.get(metric, breedId);
        if (seq !== loadSeq.current) return; // a newer request has taken over
        setPoints(curve.points);
        setSource(curve.source);
        setStored(curve.stored);
        setDirty(false);
      } catch (err) {
        if (seq !== loadSeq.current) return;
        // Clear everything that describes a curve. Leaving the previous
        // selection's rows on screen with the new selectors above them would
        // re-enable Save against the wrong target; leaving `source` set would
        // have the badge name a curve that isn't on screen; leaving `dirty` set
        // would prompt to discard edits that no longer exist.
        setPoints([]);
        setStored(false);
        setDirty(false);
        setError(handleApiError(err));
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [metric, breedId],
  );

  useEffect(() => {
    loadCurve();
  }, [loadCurve]);

  // Every write is disabled while a load is in flight: `points` still belongs to
  // the previous selection but `metric`/`breedId` have already changed, so a
  // save landing here would persist the old curve under the new target.
  const busy = saving || loading;

  // Changing measure or breed refetches and overwrites the table. Importing a
  // 100-row guide and then tapping a dropdown to check the target would
  // otherwise silently discard it — and re-typing it is exactly what import
  // exists to avoid.
  const confirmDiscard = () =>
    !dirty ||
    window.confirm("You have unsaved changes to this curve. Discard them?");

  const updatePoint = (index: number, field: keyof CurvePoint, raw: string) => {
    const next = [...points];
    next[index] = { ...next[index], [field]: raw === "" ? NaN : Number(raw) };
    setPoints(next);
    setDirty(true);
    setNotice("");
  };

  const addRow = () => {
    // The last row's age may have been blanked, which would make lastWeek + 1 NaN
    // and produce a row that can never be saved.
    // The seed is metric-dependent. 17 (so the first row lands at 18) is right
    // for hen-day production, which is undefined before lay onset — and wrong
    // for body weight and feed intake, which are defined from week 0 and are
    // usually entered as a rearing table starting at week 1.
    const seed = metric === "hdp" ? 17 : -1;
    const lastWeek = points.reduce(
      (max, p) => (Number.isFinite(p.age_weeks) ? Math.max(max, p.age_weeks) : max),
      seed,
    );
    setPoints([...points, { age_weeks: lastWeek + 1, value: 0 }]);
    setDirty(true);
  };

  const removeRow = (index: number) => {
    setPoints(points.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleSave = async () => {
    // An incomplete row is a mistake, not a row to drop. Saving the rest and
    // reporting success would quietly lose it from a hundred-row table.
    const blankRow = points.findIndex(
      (p) => !Number.isFinite(p.age_weeks) || !Number.isFinite(p.value),
    );
    if (blankRow !== -1) {
      setError(
        `Row ${blankRow + 1} is incomplete. Fill in both the age and the value, or remove the row.`,
      );
      return;
    }
    const problem = validateCurvePoints(metric, points);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await standardCurveService.save(metric, breedId, points);
      setPoints(saved.points);
      setSource(saved.source);
      setStored(saved.stored);
      setDirty(false);
      loadSaved();
      setNotice(
        breedId == null
          ? "Saved. Flocks without a breed curve of their own now use this."
          : "Saved. Flocks of this breed now use this curve.",
      );
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        // What deleting actually leaves behind, which is not the same for every
        // metric: only hdp and mortality have a shipped default to fall back
        // to. Telling the owner their flocks stay benchmarked when nothing
        // will be is the worst thing this dialog could do.
        breedId == null
          ? hasShippedDefault
            ? "Remove the farm-wide curve? Flocks fall back to the generic default."
            : `Remove the farm-wide ${metricInfo.label.toLowerCase()} curve? There is no generic default for it — flocks will have no standard for this measure until you load one.`
          : hasShippedDefault
            ? "Remove this breed's curve? Flocks of this breed fall back to the farm-wide curve, or to the generic default if there isn't one."
            : `Remove this breed's ${metricInfo.label.toLowerCase()} curve? Flocks of this breed fall back to the farm-wide curve — and if there isn't one, to no standard at all.`,
      )
    )
      return;
    setSaving(true);
    setError("");
    try {
      await standardCurveService.reset(metric, breedId);
      // loadCurve clears the notice unless told to keep it, so set it first and
      // reload with the flag — otherwise the confirmation never appears.
      setNotice("Removed. Showing the curve that applies now.");
      await loadCurve(true);
      loadSaved();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!confirmDiscard()) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setError("");
    setNotice("");
    try {
      const parsed = await standardCurveService.importCsv(metric, file);
      setPoints(parsed.points);
      setDirty(true);
      setNotice(
        `Read ${parsed.points.length} rows from ${file.name}. Check them, then save.`,
      );
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      // Let the same file be picked again after a correction.
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const selectClass =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

  return (
    <>
      <PageMeta
        title="Breed standards | Poultry Book"
        description="The expected production and mortality curves each flock is measured against."
      />
      <PageBreadcrumb pageTitle="Breed standards" />

      <div className="space-y-6">
        <div className="card p-5 lg:p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Every flock is compared against the expected value for its age. Enter
            your strain's breeder-guide figures here and the comparison becomes
            specific to your birds instead of a published average.
          </p>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="curve-metric">Measure</Label>
              <select
                id="curve-metric"
                className={selectClass}
                value={metric}
                onChange={(e) => {
                  if (!confirmDiscard()) return;
                  setMetric(e.target.value as CurveMetric);
                }}
              >
                {CURVE_METRICS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="curve-breed">Applies to</Label>
              <select
                id="curve-breed"
                className={selectClass}
                value={breedId ?? ""}
                onChange={(e) => {
                  if (!confirmDiscard()) return;
                  setBreedId(e.target.value === "" ? null : Number(e.target.value));
                }}
              >
                <option value="">All breeds (farm-wide)</option>
                {breeds.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {metricInfo.help}
          </p>

          {/* Suppressed on error: after a failed load nothing is in force and we
              don't know what is — naming any curve, including the default, would
              be a claim the page can't stand behind. */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {error ? (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Couldn&rsquo;t load this curve.
              </span>
            ) : (
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                loading
                  ? "bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-gray-400"
                  : sourceTone(source)
              }`}
            >
              {loading ? "Loading…" : `In force: ${sourceLabel(source)}`}
            </span>
            )}
            {!error && !loading && source === "default" && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Nothing saved yet — the rows below are the default, ready to edit.
              </span>
            )}
          </div>
        </div>

        {savedCurves.length > 0 && (
          <div className="card p-5 lg:p-6">
            <h3 className="mb-3 text-base font-medium text-gray-800 dark:text-white/90">
              Curves you have entered
            </h3>
            <ul className="flex flex-wrap gap-2">
              {savedCurves.map((c) => {
                const breedName =
                  c.breed_id == null
                    ? "All breeds"
                    : (breeds.find((b) => b.id === c.breed_id)?.name ?? `Breed ${c.breed_id}`);
                return (
                  <li key={`${c.metric}-${c.breed_id ?? "farm"}`}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!confirmDiscard()) return;
                        setMetric(c.metric);
                        setBreedId(c.breed_id);
                      }}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:border-brand-300 hover:text-brand-600 dark:border-gray-700 dark:text-gray-300"
                    >
                      {curveMetricLabel(c.metric)} &middot;{" "}
                      <span className="font-medium">{breedName}</span>{" "}
                      <span className="text-gray-400">({c.points.length} rows)</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Anything not listed here uses the generic curve shipped with the app, where
              there is one. Body weight, egg weight and feed intake have no generic
              curve — they are too strain-specific to average — so they have no standard
              until you load your breeder guide.
            </p>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400"
          >
            {error}
          </div>
        )}
        {notice && (
          <div
            role="status"
            className="rounded-lg border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700 dark:border-success-500/30 dark:bg-success-500/10 dark:text-success-400"
          >
            {notice}
          </div>
        )}

        <div className="card p-5 lg:p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-base font-medium text-gray-800 dark:text-white/90">
              {metricInfo.label} by age
            </h3>
            {isOwner && (
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileRef}
                  id="curve-csv"
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleImport}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => fileRef.current?.click()}
                >
                  Import CSV
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={addRow}>
                  Add row
                </Button>
                {/* Only a curve stored at THIS level can be removed. A breed
                    showing an inherited farm-wide curve has nothing of its own
                    to reset, and offering it would 404. */}
                {stored && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={handleReset}>
                    Remove this curve
                  </Button>
                )}
                <Button size="sm" onClick={handleSave} disabled={busy}>
                  {saving ? "Saving…" : "Save curve"}
                </Button>
              </div>
            )}
          </div>

          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            A CSV needs two columns: age in weeks, then the value. Extra columns
            and a header row are ignored, so a breeder guide can be pasted in
            whole. Importing fills the table below — nothing is saved until you
            press Save.
          </p>

          {loading ? (
            <p className="py-8 text-center text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="max-w-2xl overflow-x-auto">
              <Table>
                <TableHeader className="border-b border-gray-100 dark:border-gray-800">
                  <TableRow>
                    <TableCell
                      isHeader
                      className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Age (weeks)
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      {metricInfo.label} ({metricInfo.unit})
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      {""}
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {points.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="px-4 py-2">
                        <Input
                          type="number"
                          min={0}
                          max={200}
                          aria-label={`Age in weeks, row ${i + 1}`}
                          value={Number.isFinite(p.age_weeks) ? p.age_weeks : ""}
                          disabled={!isOwner}
                          onChange={(e) => updatePoint(i, "age_weeks", e.target.value)}
                        />
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        <Input
                          type="number"
                          step="0.01"
                          min={0}
                          max={metricInfo.max}
                          aria-label={`${metricInfo.label}, row ${i + 1}`}
                          value={Number.isFinite(p.value) ? p.value : ""}
                          disabled={!isOwner}
                          onChange={(e) => updatePoint(i, "value", e.target.value)}
                        />
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        {isOwner && (
                          <button
                            type="button"
                            onClick={() => removeRow(i)}
                            aria-label={`Remove row ${i + 1}`}
                            className="text-sm text-gray-400 hover:text-error-500"
                          >
                            Remove
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {points.length === 0 && (
                    <TableRow>
                      <TableCell className="px-4 py-6 text-sm text-gray-500">
                        No rows yet. Import a CSV or add rows.
                      </TableCell>
                      <TableCell className="px-4 py-6">{""}</TableCell>
                      <TableCell className="px-4 py-6">{""}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
